<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import Modal from '../../../../components/Modal.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import PipelineSelector from './PipelineSelector.vue';
import PipelineSettings from './PipelineSettings.vue';
import PipelineFollowUpRules from './PipelineFollowUpRules.vue';
import PipelineWebhooks from './PipelineWebhooks.vue';
import BulkDeleteModal from './BulkDeleteModal.vue';
import BulkMoveModal from './BulkMoveModal.vue';
import BulkAddModal from './BulkAddModal.vue';
import BulkPriorityModal from './BulkPriorityModal.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineForm from './PipelineForm.vue';
import BulkSendMessageModal from './BulkSendMessageModal.vue';
import PipelineCardImportModal from './PipelineCardImportModal.vue';
import PipelineTrashModal from './PipelineTrashModal.vue';
import { downloadBlob } from 'dashboard/helper/downloadHelper';
import { isSet, parseDateOnlyLocal } from 'dashboard/helper/filterHelper';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  currentStage: {
    type: String,
    default: '',
  },
  searchResults: {
    type: Object,
    default: () => ({ total: 0, stages: {} }),
  },
  columns: {
    type: Array,
    default: () => [],
  },
  activeFilters: {
    type: Object,
    default: null,
  },
  currentView: {
    type: String,
    default: 'pipeline',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  'filter',
  'itemCreated',
  'search',
  'itemsUpdated',
  'switchView',
  'newToastMessage',
  'startTour',
]);

// Função utilitária de debounce
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const { t } = useI18n();
const store = useStore();
const { isStacklab } = useConfig();

// User-scoped localStorage key prefix to isolate data between users
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const getUserScopedKey = columnId =>
  `u${currentUserId.value}_a${currentAccountId.value}_pipeline_column_${columnId}_settings`;

// Adicionar um watcher para monitorar mudanças
watch(
  () => isStacklab,
  () => {}
);

const showAddModal = ref(false);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

const showSettingsModal = ref(false);
const showFollowUpRulesModal = ref(false);
const showWebhooksModal = ref(false);
const showImportModal = ref(false); // NOOVI: import CSV de cards (Gap C — fase-10)
const showTrashModal = ref(false); // NOOVI: lixeira por pipeline (admin-only)

const showSearchInput = ref(false);
const searchQuery = ref('');

const showBulkActions = ref(false);
const bulkActionsButtonRef = ref(null);
const bulkActionsDropdownStyle = ref({});

const showHiddenStages = ref(false);

const bulkActions = computed(() => [
  {
    id: 'move',
    label: t('PIPELINE.BULK_ACTIONS.MOVE.TITLE'),
    icon: 'arrow-right',
  },
  {
    id: 'set_priority',
    label: t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.TITLE'),
    icon: 'flag',
  },
  {
    id: 'delete',
    label: t('PIPELINE.BULK_ACTIONS.DELETE.TITLE'),
    icon: 'delete',
  },
  {
    id: 'show_hidden',
    label: t('PIPELINE.BULK_ACTIONS.SHOW_HIDDEN'),
    icon: 'eye-show',
  },
  {
    id: 'send_message',
    label: t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.TITLE'),
    icon: 'chat',
  },
]);

const filteredBulkActions = computed(() => {
  return bulkActions.value.filter(
    action => action.id !== 'send_message' || isStacklab === true
  );
});

const showBulkDeleteModal = ref(false);
const showBulkMoveModal = ref(false);
const showBulkAddModal = ref(false);
const showBulkPriorityModal = ref(false);

const showSettingsDropdown = ref(false);
const showAddDropdown = ref(false);

// Account-aware (achado Codex): papel BASE 'administrator' + custom_role_id que
// não concede admin é uma divergência que já vazou gates admin-only em prod.
// Admin de verdade = papel administrator E sem custom role (o backend/Pundit é
// o guarda real; isto só evita exibir ações admin-only — import/export/webhooks/
// lixeira — a quem não é admin).
const userIsAdmin = computed(
  () =>
    store.getters.getCurrentRole === 'administrator' &&
    !store.getters.getCurrentCustomRoleId
);

const settingsOptions = computed(() => {
  const options = [
    {
      id: 'pipeline_settings',
      label: t('PIPELINE.MORE_OPTIONS.PIPELINE_SETTINGS'),
      icon: 'settings',
    },
  ];

  if (isStacklab === true) {
    options.push({
      id: 'message_templates',
      label: t('PIPELINE.MORE_OPTIONS.MESSAGE_TEMPLATES'),
      icon: 'document',
    });
  }

  // Add Follow-Up Rules option
  options.push({
    id: 'follow_up_rules',
    label: t('PIPELINE.MORE_OPTIONS.FOLLOW_UP_RULES'),
    icon: 'calendar-clock',
  });

  // Add Webhooks option (admin only)
  if (userIsAdmin.value) {
    options.push({
      id: 'webhooks',
      label: t('PIPELINE_WEBHOOK.TITLE'),
      icon: 'webhook',
    });
  }

  // NOOVI: Import de cards via CSV (admin only) — Gap C — fase-10.
  if (userIsAdmin.value) {
    options.push({
      id: 'import_cards',
      label: t('PIPELINE.IMPORT.MENU_LABEL'),
      icon: 'arrow-upload',
    });
  }

  // NOOVI: Lixeira por pipeline (admin only) — cards descartados, restauração e
  // exclusão definitiva. Retenção de 30 dias.
  if (userIsAdmin.value) {
    options.push({
      id: 'trash',
      label: t('PIPELINE.TRASH.MENU_LABEL'),
      icon: 'trash',
    });
  }

  // NOOVI: Export de cards para CSV (Gap C — export). Disponível a todos —
  // o backend respeita policy_scope (cada um exporta só o que enxerga).
  options.push({
    id: 'export_cards',
    label: t('PIPELINE.EXPORT.MENU_LABEL'),
    icon: 'arrow-download',
  });

  if (userIsAdmin.value) {
    options.unshift({
      id: 'manage_pipelines',
      label: t('PIPELINE.MORE_OPTIONS.MANAGE_PIPELINES'),
      icon: 'folder',
    });
  }

  // Add Start Tour option at the end
  options.push({
    id: 'start_tour',
    label: t('PIPELINE.TOUR.START'),
    icon: 'info',
  });

  return options;
});

const closeDropdowns = () => {
  showSettingsDropdown.value = false;
  showAddDropdown.value = false;
  showBulkActions.value = false;
};

const handleMessageTemplates = () => {
  emit('switchView', 'templates');
};

// NOOVI: exporta os cards (CSV) respeitando os filtros ativos do board.
const isExporting = ref(false);
const filenameFromDisposition = disposition => {
  const match = /filename="?([^"]+)"?/.exec(disposition || '');
  return match?.[1];
};
const exportCards = async () => {
  if (isExporting.value) return;
  isExporting.value = true;
  try {
    const pipelineId = selectedPipeline.value?.id;
    const response = await PipelineCardsAPI.exportCards(
      pipelineId,
      props.activeFilters
    );
    // Preserva o nome com timestamp que o backend define no Content-Disposition.
    const fileName =
      filenameFromDisposition(response.headers?.['content-disposition']) ||
      'pipeline_cards_export.csv';
    downloadBlob(fileName, response.data);
  } catch (error) {
    emit('newToastMessage', {
      message: t('PIPELINE.EXPORT.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isExporting.value = false;
  }
};

const handleSettingsOption = option => {
  if (option.id === 'pipeline_settings') {
    showSettingsModal.value = true;
  } else if (option.id === 'manage_pipelines') {
    emit('switchView', 'pipelines');
  } else if (option.id === 'message_templates') {
    handleMessageTemplates();
  } else if (option.id === 'follow_up_rules') {
    showFollowUpRulesModal.value = true;
  } else if (option.id === 'webhooks') {
    showWebhooksModal.value = true;
  } else if (option.id === 'import_cards') {
    showImportModal.value = true;
  } else if (option.id === 'trash') {
    showTrashModal.value = true;
  } else if (option.id === 'export_cards') {
    exportCards();
  } else if (option.id === 'start_tour') {
    emit('startTour');
  }
  showSettingsDropdown.value = false;
};

const handleSettingsClick = () => {
  const wasOpen = showSettingsDropdown.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showSettingsDropdown.value = !wasOpen;
};

const handleBulkActions = () => {
  const wasOpen = showBulkActions.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
    if (bulkActionsButtonRef.value) {
      const rect = bulkActionsButtonRef.value.getBoundingClientRect();
      bulkActionsDropdownStyle.value = {
        top: `${rect.bottom + 6}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
    }
  }
  showBulkActions.value = !wasOpen;
};

const showSendMessageModal = ref(false);

// Função para filtrar itens com conversa
const itemsWithConversation = computed(() => {
  return props.pipelineCards.filter(item => item.item_details?.conversation_id);
});

const selectBulkAction = action => {
  if (action.id === 'show_hidden') {
    showHiddenStages.value = !showHiddenStages.value;

    // Verifica se existem colunas ocultas antes de alterar (user-scoped)
    const hasHiddenColumns = props.columns.some(column => {
      const settings = JSON.parse(
        localStorage.getItem(getUserScopedKey(column.id)) || '{}'
      );
      return settings.hideColumn === true;
    });

    // Só altera se existirem colunas ocultas ou se estiver desativando
    if (hasHiddenColumns || !showHiddenStages.value) {
      props.columns.forEach(column => {
        const settings = JSON.parse(
          localStorage.getItem(getUserScopedKey(column.id)) || '{}'
        );

        // Só altera se a coluna estiver oculta
        if (settings.hideColumn === true) {
          settings.hideColumn = !showHiddenStages.value;
          localStorage.setItem(
            getUserScopedKey(column.id),
            JSON.stringify(settings)
          );
        }
      });

      emit('itemsUpdated');
    }
  } else if (action.id === 'delete') {
    showBulkDeleteModal.value = true;
  } else if (action.id === 'move') {
    showBulkMoveModal.value = true;
  } else if (action.id === 'add') {
    showBulkAddModal.value = true;
  } else if (action.id === 'set_priority') {
    showBulkPriorityModal.value = true;
  } else if (action.id === 'send_message') {
    if (itemsWithConversation.value.length === 0) {
      emit('newToastMessage', {
        message: t('PIPELINE.NO_ITEMS_WITH_CONVERSATION'),
        action: { type: 'warning' },
      });
      return;
    }
    showSendMessageModal.value = true;
  }
  showBulkActions.value = false;
};

const handleCloseSettings = () => {
  showSettingsModal.value = false;
};

const showNewPipelineModal = ref(false);

const handleAdd = () => {
  const wasOpen = showAddDropdown.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showAddDropdown.value = !wasOpen;
};

const handleNewItem = () => {
  if (!selectedPipeline.value?.id) {
    emit('newToastMessage', {
      message: t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED'),
      action: { type: 'error' },
    });
    return;
  }
  showAddModal.value = true;
  showAddDropdown.value = false;
};

const handleNewPipeline = () => {
  showNewPipelineModal.value = true;
  showAddDropdown.value = false;
};

const handlePipelineSaved = () => {
  showNewPipelineModal.value = false;
  emit('itemsUpdated');
};

const handleItemCreated = async item => {
  emit('itemCreated', item);
  showAddModal.value = false;
};

// Watch para manter o input visível enquanto houver busca
watch(searchQuery, newValue => {
  if (newValue) {
    showSearchInput.value = true;
  }
});

const onSearch = debounce(e => {
  const query = e.target.value;
  searchQuery.value = query;
  emit('search', query);
}, 300);

const handleBulkDelete = async payload => {
  // Fase 4.1 — payload now is `{ cardIds, pipelineStage }` (object).
  // Backward-compat: if an old caller still sends just an array, normalize.
  const { cardIds, pipelineStage = null } = Array.isArray(payload)
    ? { cardIds: payload }
    : payload || {};
  try {
    if (!cardIds?.length) return;
    // Single bulk endpoint replaces N parallel deleteItem requests
    await PipelineCardsAPI.bulkDelete({ cardIds, pipelineStage });
    showBulkDeleteModal.value = false;
    emit('itemsUpdated');
  } catch (error) {
    // Error handled silently — UI shows generic toast via parent if needed
  }
};

const handleBulkSetPriority = async payload => {
  // Fase 4.2 — payload is { cardIds, pipelineStage, priority }
  const { cardIds, pipelineStage = null, priority } = payload || {};
  try {
    if (!cardIds?.length || !priority) return;
    await PipelineCardsAPI.bulkSetPriority({
      cardIds,
      pipelineStage,
      priority,
    });
    // Atualizar localmente no store sem depender do broadcast WebSocket
    // (o cable ignora eventos do próprio usuário). O backend retorna apenas
    // contagem — não os cards — então atualizamos via cardIds + priority.
    cardIds.forEach(id => {
      store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', {
        id,
        item_details: {
          ...(store.getters['pipelineCards/getPipelineCardItems'].find(
            c => c.id === id
          )?.item_details || {}),
          priority,
        },
      });
    });
    showBulkPriorityModal.value = false;
    emit('itemsUpdated');
  } catch (error) {
    // Error handled silently
  }
};

const handleBulkMove = async ({ itemIds, stageId }) => {
  try {
    await Promise.all(
      itemIds.map(id => PipelineCardsAPI.moveToStage(id, stageId))
    );
    showBulkMoveModal.value = false;
    emit('itemsUpdated');
  } catch (error) {
    // Error handled silently
  }
};

const handleBulkItemsCreated = async () => {
  showBulkAddModal.value = false;
  emit('itemsUpdated');
};

// Função para obter a cor da etapa
const getStageColor = stageName => {
  const column = props.columns.find(col => col.title === stageName);
  return column?.color ? `${column.color}` : '#64748B';
};

// Computed para contar filtros ativos
const activeFiltersCount = computed(() => {
  if (!props.activeFilters) return 0;

  let count = 0;
  if (props.activeFilters.labels?.length) count += 1;
  if (props.activeFilters.priority?.length) count += 1;
  if (props.activeFilters.stages?.length) count += 1;
  if (
    isSet(props.activeFilters.value?.min) ||
    isSet(props.activeFilters.value?.max)
  )
    count += 1;
  if (props.activeFilters.agent_id) count += 1;
  if (props.activeFilters.date?.start || props.activeFilters.date?.end)
    count += 1;
  if (props.activeFilters.status) count += 1;
  if (props.activeFilters.sla_exceeded) count += 1;

  return count;
});

// Computed para gerar descrição dos filtros ativos
const activeFiltersTooltip = computed(() => {
  if (!props.activeFilters || activeFiltersCount.value === 0) {
    return t('PIPELINE.FILTERS.NO_FILTERS');
  }

  const descriptions = [];

  if (props.activeFilters.labels?.length) {
    descriptions.push(
      `${t('PIPELINE.FILTER.LABELS.LABEL')}: ${props.activeFilters.labels.join(', ')}`
    );
  }

  if (props.activeFilters.priority?.length) {
    const priorities = props.activeFilters.priority
      .map(p => t(`PIPELINE.PRIORITY_LABELS.${p.toUpperCase()}`))
      .join(', ');
    descriptions.push(`${t('PIPELINE.FORM.PRIORITY.LABEL')}: ${priorities}`);
  }

  if (
    isSet(props.activeFilters.value?.min) ||
    isSet(props.activeFilters.value?.max)
  ) {
    const min = isSet(props.activeFilters.value.min)
      ? props.activeFilters.value.min
      : '0';
    const max = isSet(props.activeFilters.value.max)
      ? props.activeFilters.value.max
      : '∞';
    descriptions.push(`${t('PIPELINE.FORM.VALUE.LABEL')}: ${min} - ${max}`);
  }

  if (props.activeFilters.agent_id) {
    const agent = selectedPipeline.value?.settings?.agents?.find(
      a => a.id === props.activeFilters.agent_id
    );
    const agentName = agent?.name || t('PIPELINE.FORM.AGENT.LABEL');
    descriptions.push(`${t('PIPELINE.FORM.AGENT.LABEL')}: ${agentName}`);
  }

  if (props.activeFilters.date?.start || props.activeFilters.date?.end) {
    // parse local + formato fixo dd/MM/yyyy (toLocaleDateString seguia o
    // locale do browser e o parse UTC recuava 1 dia — QA ciclo 9).
    const formatChipDate = value => {
      const parsed = parseDateOnlyLocal(value);
      if (!parsed) return '...';
      const dd = String(parsed.getDate()).padStart(2, '0');
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}/${parsed.getFullYear()}`;
    };
    const start = props.activeFilters.date.start
      ? formatChipDate(props.activeFilters.date.start)
      : '...';
    const end = props.activeFilters.date.end
      ? formatChipDate(props.activeFilters.date.end)
      : '...';
    descriptions.push(`${t('PIPELINE.DEADLINE')}: ${start} - ${end}`);
  }

  if (props.activeFilters.stages?.length) {
    descriptions.push(
      `${t('PIPELINE.STAGE.LABEL')}: ${props.activeFilters.stages.join(', ')}`
    );
  }

  if (props.activeFilters.status) {
    descriptions.push(
      `${t('PIPELINE.FILTERS.STATUS.LABEL')}: ${t(`PIPELINE.FILTERS.STATUS.${props.activeFilters.status.toUpperCase()}`)}`
    );
  }

  if (props.activeFilters.sla_exceeded) {
    descriptions.push(t('PIPELINE.SLA.FILTER_LABEL'));
  }

  return descriptions.join('\n');
});

const handleViewChange = view => {
  emit('switchView', view);
};

// Map bulk action icon names to Lucide icons
const getBulkActionIcon = iconName => {
  const iconMap = {
    add: 'i-lucide-plus',
    'arrow-right': 'i-lucide-arrow-right',
    flag: 'i-lucide-flag',
    delete: 'i-lucide-trash-2',
    'eye-show': 'i-lucide-eye',
    chat: 'i-lucide-message-circle',
  };
  return iconMap[iconName] || 'i-lucide-circle';
};

// Map settings option icon names to Lucide icons
const getSettingsIcon = iconName => {
  const iconMap = {
    settings: 'i-lucide-settings',
    document: 'i-lucide-file-text',
    folder: 'i-lucide-folder',
    clock: 'i-lucide-clock',
    'calendar-clock': 'i-lucide-calendar-clock',
    'arrow-upload': 'i-lucide-upload',
    'arrow-download': 'i-lucide-download',
    info: 'i-lucide-info',
    webhook: 'i-lucide-webhook',
    trash: 'i-lucide-trash-2',
  };
  return iconMap[iconName] || 'i-lucide-circle';
};

// Usar getter do store para obter agentes
const filteredAgents = computed(() => {
  // Usar os agentes do funil atual
  const agentsFromPipeline = selectedPipeline.value?.settings?.agents || [];

  if (!searchQuery.value) return agentsFromPipeline;

  const query = searchQuery.value.toLowerCase();
  return agentsFromPipeline.filter(
    agent =>
      agent.name.toLowerCase().includes(query) ||
      agent.email.toLowerCase().includes(query)
  );
});

const toggleAgent = async agent => {
  try {
    const currentAgents = selectedPipeline.value.settings?.agents || [];
    const index = currentAgents.findIndex(a => a.id === agent.id);

    let updatedAgents;
    if (index === -1) {
      updatedAgents = [...currentAgents, agent];
    } else {
      updatedAgents = currentAgents.filter(a => a.id !== agent.id);
    }

    // Prepara o payload mantendo todos os dados existentes
    const payload = {
      ...selectedPipeline.value,
      settings: {
        ...selectedPipeline.value.settings,
        agents: updatedAgents,
        optimization_history:
          selectedPipeline.value.settings?.optimization_history || [],
      },
      stages: Object.entries(selectedPipeline.value.stages || {}).reduce(
        (acc, [id, stage]) => ({
          ...acc,
          [id]: {
            ...stage,
            message_templates: stage.message_templates || [], // Mantém os templates
          },
        }),
        {}
      ),
    };

    // Atualiza o funil
    await PipelinesAPI.update(selectedPipeline.value.id, payload);
    await store.dispatch('pipelines/fetch', { force: true });
  } catch (error) {
    // Error handled silently
  }
};

onMounted(() => {
  document.addEventListener('click', event => {
    const target = event.target;
    const isDropdownClick = target.closest(
      '.settings-selector, .bulk-actions-selector, .pipeline-add-item-trigger'
    );

    if (!isDropdownClick) {
      closeDropdowns();
    }
  });

  // Listen for global dropdown close event
  emitter.on('closeAllPipelineDropdowns', closeDropdowns);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns);
  emitter.off('closeAllPipelineDropdowns', closeDropdowns);
});

// Adicionar ref para controlar modal de compartilhamento
const showShareModal = ref(false);

// Total pipeline value from all cards
const totalPipelineValue = computed(() => {
  return props.pipelineCards.reduce((total, card) => {
    const value = parseFloat(card.item_details?.value) || 0;
    return total + value;
  }, 0);
});
const formattedPipelineTotal = computed(() => {
  if (!totalPipelineValue.value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(totalPipelineValue.value);
});

// Função para enviar mensagem em massa
const handleMessageSent = async () => {
  showSendMessageModal.value = false;
  emit('newToastMessage', {
    message: t('PIPELINE.MESSAGES.MESSAGES_SENT_SUCCESS'),
    action: { type: 'success' },
  });
};

// Adicionar ref para controlar modal de visualização
const showViewModal = ref(false);
</script>

<template>
  <header
    class="relative z-50 flex items-center h-12 px-3 max-md:px-2 border-b border-n-weak bg-n-background"
  >
    <!-- LEFT ZONE: View switcher + Pipeline context -->
    <div class="flex items-center gap-2 min-w-0 flex-1">
      <!-- Mobile: View selector button -->
      <button
        class="md:hidden flex items-center gap-1 px-2 py-1.5 rounded-lg bg-n-alpha-2 text-n-slate-12 border border-n-weak hover:bg-n-alpha-3 active:bg-n-alpha-4"
        @click="showViewModal = true"
      >
        <Icon
          :icon="
            currentView === 'pipeline'
              ? 'i-lucide-kanban'
              : currentView === 'list'
                ? 'i-lucide-list'
                : 'i-lucide-calendar'
          "
          class="size-4"
        />
        <Icon icon="i-lucide-chevron-down" class="size-3" />
      </button>

      <!-- Desktop: View mode pill tabs -->
      <nav class="hidden md:flex items-center rounded-lg bg-n-alpha-1 p-0.5">
        <button
          v-for="view in [
            {
              id: 'pipeline',
              icon: 'i-lucide-kanban',
              label: t('PIPELINE.VIEW_MODES.PIPELINE'),
              desc: t('PIPELINE.VIEW_MODES.PIPELINE_DESC'),
            },
            {
              id: 'list',
              icon: 'i-lucide-list',
              label: t('PIPELINE.VIEW_MODES.LIST'),
              desc: t('PIPELINE.VIEW_MODES.LIST_DESC'),
            },
            {
              id: 'agenda',
              icon: 'i-lucide-calendar',
              label: t('PIPELINE.VIEW_MODES.AGENDA'),
              desc: t('PIPELINE.VIEW_MODES.AGENDA_DESC'),
            },
            {
              id: 'dashboard',
              icon: 'i-lucide-layout-dashboard',
              label: t('PIPELINE.VIEW_MODES.DASHBOARD'),
              desc: t('PIPELINE.VIEW_MODES.DASHBOARD_DESC'),
            },
          ]"
          :key="view.id"
          v-tooltip="{
            content: view.desc,
            delay: { show: 400, hide: 0 },
            placement: 'bottom',
          }"
          class="relative flex items-center justify-center size-7 rounded-md transition-all duration-150"
          :class="[
            currentView === view.id
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          :aria-label="view.label"
          @click="emit('switchView', view.id)"
        >
          <Icon :icon="view.icon" class="size-3.5" />
        </button>
      </nav>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak" />

      <!-- Pipeline selector -->
      <PipelineSelector class="md:block hidden" />

      <!-- Total value badge -->
      <div
        v-if="formattedPipelineTotal"
        v-tooltip="{
          content: t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_TOOLTIP', {
            count: pipelineCards.length,
            total: formattedPipelineTotal,
          }),
          delay: { show: 300, hide: 0 },
          placement: 'bottom',
        }"
        class="hidden lg:flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-n-teal-3/60 cursor-help"
      >
        <Icon icon="i-lucide-trending-up" class="size-3 text-n-teal-11" />
        <span class="text-xs font-medium text-n-teal-11 tabular-nums">
          {{ formattedPipelineTotal }}
        </span>
      </div>
    </div>

    <!-- RIGHT ZONE: Search + Actions -->
    <div class="flex items-center gap-1 max-md:gap-0.5">
      <!-- Search (always visible) -->
      <div class="md:flex hidden items-center relative">
        <div
          class="h-7 w-44 rounded-md border border-n-weak bg-n-alpha-1 flex items-center overflow-hidden transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2"
        >
          <Icon
            icon="i-lucide-search"
            class="size-3.5 text-n-slate-9 ml-2 flex-shrink-0"
          />
          <input
            id="pipeline-search"
            v-model="searchQuery"
            name="pipeline_search"
            type="search"
            class="w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 reset-base no-margin"
            :placeholder="t('PIPELINE.SEARCH.INPUT_PLACEHOLDER')"
            :aria-label="t('PIPELINE.SEARCH.INPUT_PLACEHOLDER')"
            @input="onSearch"
          />
        </div>
        <!-- Search results badges -->
        <div
          v-if="searchQuery && searchResults.total > 0"
          class="flex items-center gap-1 ml-1 flex-shrink-0"
        >
          <span
            class="flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-n-slate-12 text-n-background whitespace-nowrap"
          >
            {{ searchResults.total }}
          </span>
          <span
            v-for="(count, stageName) in searchResults.stages"
            :key="stageName"
            class="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md text-white whitespace-nowrap"
            :style="{ backgroundColor: getStageColor(stageName) }"
          >
            {{ stageName }}
            <span class="opacity-70">{{ count }}</span>
          </span>
        </div>
      </div>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak mx-0.5" />

      <!-- Bulk Actions -->
      <div
        ref="bulkActionsButtonRef"
        class="relative inline-block bulk-actions-selector"
      >
        <Button
          v-tooltip="{
            content: t('PIPELINE.BULK_ACTIONS.TITLE'),
            delay: { show: 400, hide: 0 },
            placement: 'bottom',
          }"
          variant="ghost"
          color="slate"
          size="xs"
          @click="handleBulkActions"
        >
          <template #icon>
            <Icon icon="i-lucide-zap" class="size-3.5" />
          </template>
        </Button>

        <!-- Bulk Actions Dropdown — Teleported to body to avoid overflow clipping -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="showBulkActions"
              class="fixed z-dropdown-overlay min-w-[220px] p-1 rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-top-right"
              :style="bulkActionsDropdownStyle"
            >
              <!-- UX B1 (2026-04-22): padronizado com `text-left`
                   no button (como o dropdown de Settings). Já tinha
                   `flex-1 text-left` no span do label desde antes. -->
              <button
                v-for="action in filteredBulkActions"
                :key="action.id"
                class="w-full min-h-[44px] flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 transition-colors"
                @click="selectBulkAction(action)"
              >
                <Icon
                  :icon="getBulkActionIcon(action.icon)"
                  class="size-4 text-n-slate-10 flex-shrink-0"
                />
                <span class="flex-1 text-left">{{ action.label }}</span>
                <span
                  v-if="action.id === 'show_hidden'"
                  class="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  :class="{
                    'bg-n-blue-9 text-white': showHiddenStages,
                    'bg-n-alpha-2 text-n-slate-10': !showHiddenStages,
                  }"
                >
                  {{
                    showHiddenStages
                      ? t('PIPELINE.TOGGLE_STATUS.ACTIVE')
                      : t('PIPELINE.TOGGLE_STATUS.INACTIVE')
                  }}
                </span>
              </button>
            </div>
          </Transition>
        </Teleport>
      </div>

      <!-- Filter -->
      <Button
        variant="ghost"
        color="slate"
        size="xs"
        class="relative"
        data-tour="filter-button"
        :title="activeFiltersTooltip"
        @click="$emit('filter')"
      >
        <template #icon>
          <Icon icon="i-lucide-filter" class="size-3.5" />
        </template>
        <span
          v-if="activeFiltersCount > 0"
          class="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[9px] font-semibold bg-n-blue-9 text-white rounded-full leading-none"
          :title="activeFiltersTooltip"
        >
          {{ activeFiltersCount }}
        </span>
      </Button>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak mx-0.5" />

      <!-- Add item -->
      <div class="relative md:block hidden">
        <Button
          variant="solid"
          color="blue"
          size="xs"
          class="pipeline-add-item-trigger"
          @click="handleAdd"
        >
          <template #icon>
            <Icon icon="i-lucide-plus" class="size-3.5" />
          </template>
        </Button>

        <!-- Add Dropdown -->
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showAddDropdown"
            class="absolute right-0 top-full mt-1.5 bg-n-solid-2 rounded-xl shadow-lg border border-n-weak p-1 w-48 z-10 origin-top-right"
            @click.stop
          >
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="handleNewItem"
            >
              <Icon icon="i-lucide-plus" class="size-4 text-n-slate-10" />
              {{ t('PIPELINE.ADD_ITEM') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="handleNewPipeline"
            >
              <Icon icon="i-lucide-kanban" class="size-4 text-n-slate-10" />
              {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
            </button>
          </div>
        </Transition>
      </div>

      <!-- More options -->
      <div class="relative inline-block settings-selector">
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          :title="!userIsAdmin ? t('PIPELINE.ADMIN_ONLY') : ''"
          @click="handleSettingsClick"
        >
          <template #icon>
            <Icon icon="i-lucide-settings" class="size-3.5" />
          </template>
        </Button>

        <!-- Settings Dropdown -->
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showSettingsDropdown"
            class="absolute right-0 top-full z-dropdown-overlay min-w-[220px] mt-1.5 p-1 rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-top-right"
            @mouseleave="showSettingsDropdown = false"
          >
            <!-- Bug 2 refinement (2026-04-22): adicionado `text-left`
                 explícito no button + `flex-1 text-left` no span do
                 label. Antes o button herdava `text-align: center`
                 do browser default — em algumas combinações de font/
                 zoom o item "Configurações do Pipeline" parecia
                 centralizado mesmo com flex layout. -->
            <!-- UX B2 (2026-04-22): + min-h-[44px] (WCAG 2.5.5 touch
                 target). Já tinha text-left desde Bug 2 refinement. -->
            <button
              v-for="option in settingsOptions"
              :key="option.id"
              class="w-full min-h-[44px] flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 transition-colors"
              @click="handleSettingsOption(option)"
            >
              <Icon
                :icon="getSettingsIcon(option.icon)"
                class="size-4 text-n-slate-10 flex-shrink-0"
              />
              <span class="flex-1 text-left">{{ option.label }}</span>
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Menu móvel para elementos ocultos -->
    <!-- Navbar inferior removida conforme solicitado -->

    <!-- Modal para seleção de visualização -->
    <Modal
      v-model:show="showViewModal"
      size="tiny"
      @close="() => (showViewModal = false)"
    >
      <div class="p-4">
        <h3 class="text-lg font-medium mb-4 text-n-slate-12">
          {{ t('PIPELINE.VIEW_MODES.SELECT_VIEW') }}
        </h3>
        <div class="flex flex-col gap-3">
          <button
            class="flex items-center gap-3 p-3 rounded-lg text-left"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'pipeline',
              'hover:bg-n-alpha-2': currentView !== 'pipeline',
            }"
            @click="
              handleViewChange('pipeline');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-kanban" class="size-5" />
            <div>
              <div class="font-medium">
                {{ t('PIPELINE.VIEW_MODES.PIPELINE') }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.PIPELINE_DESC') }}
              </div>
            </div>
          </button>

          <button
            class="flex items-center gap-3 p-3 rounded-lg text-left"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'list',
              'hover:bg-n-alpha-2': currentView !== 'list',
            }"
            @click="
              handleViewChange('list');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-list" class="size-5" />
            <div>
              <div class="font-medium">{{ t('PIPELINE.VIEW_MODES.LIST') }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.LIST_DESC') }}
              </div>
            </div>
          </button>

          <button
            class="flex items-center gap-3 p-3 rounded-lg text-left"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'agenda',
              'hover:bg-n-alpha-2': currentView !== 'agenda',
            }"
            @click="
              handleViewChange('agenda');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-calendar" class="size-5" />
            <div>
              <div class="font-medium">
                {{ t('PIPELINE.VIEW_MODES.AGENDA') }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.AGENDA_DESC') }}
              </div>
            </div>
          </button>

          <button
            class="flex items-center gap-3 p-3 rounded-lg text-left hover:bg-n-alpha-2"
            @click="
              handleViewChange('dashboard');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-layout-dashboard" class="size-5" />
            <div>
              <div class="font-medium">{{ t('PIPELINE.DASHBOARD.TITLE') }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.DASHBOARD.SUBTITLE') }}
              </div>
            </div>
          </button>
        </div>
      </div>
    </Modal>

    <Modal v-model:show="showAddModal" @close="() => (showAddModal = false)">
      <div class="flex flex-col max-h-[85vh] overflow-hidden">
        <div class="px-5 pt-5 pb-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('PIPELINE.ADD_ITEM') }}
          </h3>
        </div>
        <PipelineCardForm
          v-if="selectedPipeline"
          :pipeline-id="selectedPipeline.id"
          :stage="currentStage"
          @saved="handleItemCreated"
          @close="showAddModal = false"
        />
        <div v-else class="text-center text-red-500 p-6">
          {{ t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED') }}
        </div>
      </div>
    </Modal>

    <PipelineSettings :show="showSettingsModal" @close="handleCloseSettings" />

    <PipelineCardImportModal
      v-model:show="showImportModal"
      :pipeline-id="selectedPipeline?.id"
      @imported="emit('itemsUpdated')"
    />

    <PipelineTrashModal
      v-model:show="showTrashModal"
      :pipeline-id="selectedPipeline?.id"
    />

    <PipelineFollowUpRules
      :show="showFollowUpRulesModal"
      :pipeline="selectedPipeline"
      @close="showFollowUpRulesModal = false"
    />

    <Modal
      v-model:show="showWebhooksModal"
      size="large"
      @close="() => (showWebhooksModal = false)"
    >
      <PipelineWebhooks
        :pipelines="selectedPipeline ? [selectedPipeline] : []"
        @close="showWebhooksModal = false"
      />
    </Modal>

    <Modal
      v-model:show="showBulkDeleteModal"
      @close="() => (showBulkDeleteModal = false)"
    >
      <BulkDeleteModal
        :items="pipelineCards"
        @close="showBulkDeleteModal = false"
        @confirm="handleBulkDelete"
      />
    </Modal>

    <Modal
      v-model:show="showBulkPriorityModal"
      @close="() => (showBulkPriorityModal = false)"
    >
      <BulkPriorityModal
        :items="pipelineCards"
        @close="showBulkPriorityModal = false"
        @confirm="handleBulkSetPriority"
      />
    </Modal>

    <Modal
      v-model:show="showBulkMoveModal"
      @close="() => (showBulkMoveModal = false)"
    >
      <BulkMoveModal
        :items="pipelineCards"
        @close="showBulkMoveModal = false"
        @confirm="handleBulkMove"
      />
    </Modal>

    <Modal
      v-model:show="showBulkAddModal"
      @close="() => (showBulkAddModal = false)"
    >
      <BulkAddModal
        :current-stage="currentStage"
        @close="showBulkAddModal = false"
        @items-created="handleBulkItemsCreated"
      />
    </Modal>

    <!-- Modal de compartilhamento -->
    <Modal
      v-model:show="showShareModal"
      size="tiny"
      @close="() => (showShareModal = false)"
    >
      <div class="p-4 space-y-4">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ t('PIPELINE.SHARE.TITLE') }}
          </h3>
        </div>

        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg pr-8 bg-n-solid-2 text-n-slate-12 placeholder-n-slate-9"
            :placeholder="t('PIPELINE.SHARE.SEARCH_AGENTS')"
          />
          <Icon
            icon="i-lucide-search"
            class="absolute right-3 top-1/2 transform -translate-y-1/2 size-4 text-n-slate-9"
          />
        </div>

        <div class="max-h-[300px] overflow-y-auto space-y-2">
          <div
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="flex items-center gap-3 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer"
            :class="{
              'bg-n-blue-3': selectedPipeline.settings?.agents?.some(
                a => a.id === agent.id
              ),
            }"
            @click="toggleAgent(agent)"
          >
            <Avatar
              :name="agent.name"
              :src="agent.avatar_url"
              :size="32"
              :status="agent.availability_status"
            />
            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm truncate text-n-slate-12">
                {{ agent.name }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ agent.email }}
              </div>
            </div>
            <Icon
              v-if="
                selectedPipeline.settings?.agents?.some(a => a.id === agent.id)
              "
              icon="i-lucide-check"
              class="size-4 text-n-blue-9"
            />
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de Novo Funil -->
    <Modal
      v-model:show="showNewPipelineModal"
      size="large"
      @close="() => (showNewPipelineModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h3>
        <PipelineForm
          @saved="handlePipelineSaved"
          @close="showNewPipelineModal = false"
        />
      </div>
    </Modal>

    <!-- Modal de Envio de Mensagem -->
    <Modal
      v-model:show="showSendMessageModal"
      size="medium"
      @close="() => (showSendMessageModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.MODAL_TITLE') }}
        </h3>
        <BulkSendMessageModal
          :items="itemsWithConversation"
          @close="showSendMessageModal = false"
          @send="handleMessageSent"
        />
      </div>
    </Modal>
  </header>
</template>

<style scoped>
/* AI Modal sizing - requires deep selector */
.ai-modal :deep(.modal__content) {
  width: 85vw;
  height: 85vh;
  max-width: 1400px;
  padding: 0;
}
</style>
